import { parseISO } from 'date-fns';
import { sort } from 'ramda';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';
import { InfoMessage } from '@/components/Message';
import { Pagination } from '@/components/Pagination';
import { DEFAULT_CURRENCIES } from '@/helpers/currency';
import { compareDates } from '@/helpers/dateTime';
import { formatWalletValue, getCurrencyCentsAndCode } from '@/helpers/wallet';
import { useCMSLoyaltyMissions } from '@/modules/loyalty';
import { useGetCommonStore } from '@/store';
import { Spinner } from '@/styles/common';
import { isBonus } from '../../helpers';
import { BonusesFiltration } from './components/BonusesFiltration';
import { BonusTable, type BonusTableRow } from './components/BonusTable';
import { ScBonusHistory } from './styled';

export const BonusHistory = () => {
  const { t } = useTranslation('bonuses');
  const tableRef = useRef<HTMLDivElement>(null);

  const { blackColor, mainColor } = useTheme();
  const useCommonStore = useGetCommonStore();

  const {
    bonusesPending,
    bonuses,
    freespinsPending,
    freespins,
    isMobileDevice,
  } = useCommonStore(
    useShallow((state) => ({
      bonusesPending: state.bonusesPending,
      bonuses: state.bonuses,
      freespinsPending: state.freespinsPending,
      freespins: state.freespins,
      isMobileDevice: state.isMobileDevice,
    }))
  );

  const [currentPage, setCurrentPage] = useState(1);

  const [currencyFilter, setCurrencyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);

  const allUserBonuses = useMemo(
    () => [...bonuses, ...freespins],
    [bonuses, freespins]
  );

  const filteredBonuses = useMemo(() => {
    if (!(bonuses || freespins)) return [];

    let allBonuses = allUserBonuses;

    if (currencyFilter) {
      allBonuses = allBonuses.filter(
        ({ currency }) => currency === currencyFilter
      );
    }

    if (statusFilter) {
      allBonuses = allBonuses.filter(({ stage }) => statusFilter === stage);
    }

    const fromDate = dateFilter[0];
    const toDate = dateFilter[1];

    if (fromDate) {
      allBonuses = allBonuses.filter(({ createdAt }) =>
        compareDates(createdAt, fromDate.toISOString())
      );
    }

    if (toDate) {
      allBonuses = allBonuses.filter(({ createdAt }) =>
        compareDates(toDate.toISOString(), createdAt)
      );
    }

    return allBonuses;
  }, [
    currencyFilter,
    dateFilter,
    freespins,
    bonuses,
    statusFilter,
    allUserBonuses,
  ]);

  const { missions, missionsPending } = useCMSLoyaltyMissions();

  const normalizedDataForTable = useMemo(() => {
    const normalizedBonuses = filteredBonuses.map((bonus) => {
      const {
        title,
        createdAt,
        validUntil,
        stage,
        activatableUntil,
        activatable,
        currency,
        groupKey,
      } = bonus;

      const matchedMission = missions.find(
        (mission) => mission.groupKeyId === groupKey
      );

      const combinedTitle = matchedMission
        ? `${title} (${matchedMission.name})`
        : title;

      const customFields = isBonus(bonus)
        ? {
            amountCents: bonus.amountCents,
            amountWagerCents: bonus.amountWagerCents,
            amountWagerRequirementCents: bonus.amountWagerRequirementCents,
          }
        : { freespinsTotal: bonus.freespinsTotal };

      const validUntilData = activatable
        ? activatableUntil
        : validUntil || activatableUntil;

      const formatedAmountCents = customFields.amountCents
        ? formatWalletValue(
            currency,
            customFields.amountCents,
            DEFAULT_CURRENCIES,
            true
          )
        : undefined;

      const wagerProgress = (() => {
        if (
          customFields.amountWagerCents !== undefined &&
          customFields.amountWagerRequirementCents !== undefined
        ) {
          const { amount: amountWager } = getCurrencyCentsAndCode(
            customFields.amountWagerCents,
            currency,
            DEFAULT_CURRENCIES
          );

          const { amount: amountWagerRequirement, currency: code } =
            getCurrencyCentsAndCode(
              customFields.amountWagerRequirementCents,
              currency,
              DEFAULT_CURRENCIES
            );

          return (
            <>
              {amountWager} / {amountWagerRequirement} {code} {t('wagered')}
            </>
          );
        }

        return <></>;
      })();

      return {
        createdAt,
        validUntil: validUntilData,
        title: combinedTitle || 'CASINO',
        sum: formatedAmountCents || `${customFields.freespinsTotal} FS`,
        stage,
        progress: wagerProgress,
      };
    });

    const sortedData = sort(
      (a: BonusTableRow, b: BonusTableRow) =>
        parseISO(b.createdAt).getTime() - parseISO(a.createdAt).getTime(),
      normalizedBonuses
    );

    return sortedData;
  }, [filteredBonuses, t, missions]);

  const rowsNumber = useMemo(() => (isMobileDevice ? 6 : 12), [isMobileDevice]);

  const pageData = useMemo(() => {
    if (!normalizedDataForTable.length) return [];

    const startIndex = (currentPage - 1) * rowsNumber;
    const endIndex = startIndex + rowsNumber;

    return normalizedDataForTable.slice(startIndex, endIndex);
  }, [currentPage, normalizedDataForTable, rowsNumber]);

  const totalPages = useMemo(() => {
    if (!normalizedDataForTable.length) return 0;

    return Math.ceil(normalizedDataForTable.length / rowsNumber);
  }, [normalizedDataForTable.length, rowsNumber]);

  const handleSelectCurrencyFilter = (currency: string) => {
    setCurrencyFilter(currency);
    setCurrentPage(1);
  };

  const handleSelectStatusFilter = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleSelectDateFilter = (dateRange: [Date | null, Date | null]) => {
    setDateFilter(dateRange);
    setCurrentPage(1);
  };

  const handleChangePage = (index: number) => {
    setCurrentPage(index);

    if (!tableRef.current) return;

    window.scrollTo({
      top: tableRef.current.getBoundingClientRect().top + window.scrollY,
      behavior: 'smooth',
    });
  };

  return (
    <ScBonusHistory>
      {bonusesPending || freespinsPending || missionsPending ? (
        <Spinner
          background={blackColor}
          color={mainColor}
          margin="16px auto"
          size={60}
        />
      ) : allUserBonuses.length ? (
        <>
          <InfoMessage message={t('historyLimitationMessage')} />
          <BonusesFiltration
            setCurrency={handleSelectCurrencyFilter}
            setDate={handleSelectDateFilter}
            setStatus={handleSelectStatusFilter}
          />
          {pageData.length > 0 ? (
            <BonusTable ref={tableRef} rows={pageData} />
          ) : (
            <div className="no-history">{t('noBonusHistory')}</div>
          )}
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              handlePageClick={handleChangePage}
              totalPages={totalPages}
            />
          )}
        </>
      ) : (
        <p className="no-history">{t('noBonusHistory')}</p>
      )}
    </ScBonusHistory>
  );
};
